import { transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { contentStyles, h3, h4, h6, paragraphRegular, paragraphSmall } from '@helpers/typography';
import SectionWrapper, { ContentType, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  --bgColour: ${brand.green.light};
  --borderRadius: ${fluid(15, 30)};
  --cardBgColour: ${brand.green.medium};
  --iconBackground: ${brand.green.gradient};
  --iconBorder: ${brand.green.default};
  --iconColour: ${brand.global.white};

  &[data-type=${ContentType.Intermediaries}] {
    --iconBackground: ${brand.blue.dark};
    --iconBorder: ${brand.blue.dark};
  }

  &[data-type=${ContentType.Savings}] {
    --bgColour: ${brand.blue.light};
    --cardBgColour: ${brand.blue.medium};
    --iconBackground: ${brand.blue.gradient};
    --iconBorder: ${brand.blue.default};
  }
`;
const CardInner = styled.div `
  ${baseGrid};

  background-color: var(--bgColour);
  border-radius: var(--borderRadius) var(--borderRadius) var(--borderRadius) 0;
  padding: ${fluid(24, 48)} ${fluid(16, 36)};

  @media ${from(Device.TabletLarge)} {
    grid-template-rows: min-content 1fr;
  }
`;
const ContentWrapper = styled.div `
  container-type: inline-size;
  grid-column: 1 / -1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 6;
    grid-row: span 2;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 1 / span 5;
  }
`;
const Title = styled.h2 `
  ${h3};

  margin: 0;
`;
const Description = styled.div `
  ${contentStyles};

  margin: 8px 0 0;
`;
const HelpList = styled.ul `
  ${paragraphRegular};
  ${fonts.proximaNova.semiBold};

  list-style-type: none;
  display: grid;
  gap: ${fluid(12, 28)} ${fluid(16, 38)};
  margin: ${fluid(24, 38)} 0 0;

  @container ${from(420)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const HelpItem = styled.li `
  display: inline-flex;
  align-items: center;
  gap: ${fluid(10, 15)};
`;
const HelpIcon = styled.div `
  background: var(--iconBackground);
  border: 2px solid var(--iconBorder);
  border-radius: 5000px;
  width: ${fluid(31, 50)};
  height: ${fluid(31, 50)};
  display: grid;
  place-content: center;

  svg {
    color: var(--iconColour);
    width: ${fluid(18, 32)};
  }
`;
const CtaWrapper = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: ${fluid(8, 24)};
  margin: ${fluid(24, 38)} 0 0;
`;
const contactColumns = css `
  grid-column: 1 / -1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 7 / span 6;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 8 / span 5;
  }
`;
const cardContainerBreakpoint = 350;
const ContactImage = styled.div `
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 5000px;
`;
const Image = styled.img `
  ${imageCoverContainer()};
`;
const ContactDetails = styled.div `
  ${contactColumns};

  background-color: var(--cardBgColour);
  border-radius: 10px 10px 10px 0;
  padding: ${fluid(16, 36)} ${fluid(12, 28)};
  container-type: inline-size;

  &:has(${ContactImage}) {
    display: grid;
    grid-template-columns: ${fluid(46, 100)} 1fr;
    column-gap: ${fluid(12, 26)};
  }

  @media ${until(Device.TabletLarge)} {
    margin-top: 32px;
  }
`;
const ContactTitle = styled.h3 `
  ${h6};
  text-wrap: pretty;

  ${ContactDetails}:has(${ContactImage}) & {
    margin: auto 0;
  }
`;
const ContactNumber = styled.a `
  ${transition('text-decoration-color')};
  ${h4};

  display: inline-block;
  margin: 16px 0 ${fluid(16, 26)};
  color: var(--sectionTitleColour);
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  grid-column: 1 / -1;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    outline-width: 1px;
    outline-style: solid;
  }

  &:focus-visible,
  &:hover {
    text-decoration-color: currentColor;
  }

  @container ${from(cardContainerBreakpoint)} {
    grid-column: 2 / span 1;
    margin-top: 4px;
  }
`;
const OpeningHours = styled.table `
  ${paragraphRegular};
  ${fonts.proximaNova.semiBold};

  margin: 0;
  grid-column: 1 / -1;

  tr td:first-of-type {
    padding-right: ${fluid(18, 50)};
  }

  tr:not(:last-of-type) td {
    padding-bottom: ${fluid(4, 6)};
  }

  @container ${from(cardContainerBreakpoint)} {
    grid-column: 2 / span 1;
    margin-right: auto;
  }
`;
const ContactDisclaimer = styled.p `
  ${contactColumns};
  ${paragraphSmall};

  margin: 8px 0 0;
`;
const ContactCardStyles = {
    CardInner,
    ContactDetails,
    ContactDisclaimer,
    ContactImage,
    ContactNumber,
    ContactTitle,
    Container,
    ContentWrapper,
    CtaWrapper,
    Description,
    HelpIcon,
    HelpItem,
    HelpList,
    Image,
    OpeningHours,
    Title,
};
export default ContactCardStyles;
